import { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from '../../../auth';
import ImageDrop from '../../ImageDrop';
import { useDragReorder } from '../../useDragReorder';
import { SectionBuilder } from '../../components/SectionBuilder';
import { useSection } from '../../useSection';

const API = '/api/prodcat';
const SLUG = 'rugged';

let _uid = 0;
const uid = () => `u${++_uid}`;

const toSlug = (model) => (model || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const newLink    = () => ({ _id: uid(), key: '', url: '' });
const newSpecSheet = (label = '새 탭') => ({ _id: uid(), label, content: '' });
const newItem    = () => ({ _id: uid(), model: '', category: '', imageKeys: [], features: '', tags: [], specSheets: [
  newSpecSheet('Technical Profile'), newSpecSheet('Specifications'), newSpecSheet('Order Information'),
], links: [] });
const newBnItem  = () => ({ _id: uid(), _bn: true, model: '', sectionContent: '', bnContent: '' });
const newSubcat  = () => ({ _id: uid(), subcat: '새 소분류', items: [newItem()] });
const newBnSubcat = () => ({ _id: uid(), subcat: '새 항목', items: [newBnItem()] });
const newCat     = (editorType = 'form') => ({ _id: uid(), intNum: null, editorType, category: '새 대분류', subcats: editorType === 'blocknote' ? [newBnSubcat()] : [newSubcat()] });

function specsToText(sections) {
  return (sections || []).map(sec => {
    const rows = (sec.specs || []).map(s => `${s.key}: ${s.value}`).join('\n');
    return sec.title ? `${sec.title}\n${rows}` : rows;
  }).join('\n\n');
}


function parseItem(it) {
  if (it._bn) {
    return { _id: uid(), _bn: true, model: it.name || it.model || '', sectionContent: it.sectionContent || it.bnContent || '', bnContent: it.sectionContent || it.bnContent || '' };
  }
  return {
    _id: uid(),
    model: it.model || '', category: it.category || '',
    imageKeys: Array.isArray(it.imageKeys) ? it.imageKeys : (it.imageKey ? [it.imageKey] : []),
    features: Array.isArray(it.features) ? it.features.join('\n') : (it.features || ''),
    tags: Array.isArray(it.tags) ? it.tags : [],
    specSheets: it.specSheets
      ? it.specSheets.map(s => ({ _id: uid(), label: s.label || '', content: s.content || '' }))
      : [
          { _id: uid(), label: 'Technical Profile', content: it.profile || '' },
          { _id: uid(), label: 'Specifications', content: specsToText(it.specSections || []) },
          { _id: uid(), label: 'Order Information', content: typeof it.orderInfo === 'string' ? it.orderInfo : '' },
        ],
    links: (it.links || []).map(l => ({ _id: uid(), key: l.key || '', url: l.url || '' })),
  };
}

function parseRow(row) {
  let data = {};
  try { data = JSON.parse(row.textCategory || '{}'); } catch (_) {}
  // 구버전 호환: {subcat, items} → {category, subcats:[{subcat, items}]}
  if (data.subcat !== undefined && data.category === undefined) {
    data = { category: data.subcat || '', subcats: [{ subcat: data.subcat || '', items: data.items || [] }] };
  }
  const editorType = data.editorType || 'form';
  return {
    intNum: row.intNum, _id: uid(),
    editorType,
    category: data.category || '',
    subcats: (data.subcats || []).map(sc => ({
      _id: uid(), subcat: sc.subcat || '',
      items: (sc.items || []).map(it => parseItem(it)),
    })),
  };
}

export default function RuggedProduct({ refreshKey = 0, onRegisterSave, onRegisterDirty, onBack }) {
  const [cats,      setCats]      = useState(null);
  const [catIdx,    setCatIdx]    = useState(0);
  const [subcatIdx, setSubcatIdx] = useState(0);
  const [itemIdx,   setItemIdx]   = useState(0);
  const [specTabIdx, setSpecTabIdx] = useState(0);
  const ref    = useRef([]);
  const delRef = useRef([]);
  const dirtyRef = useRef(false);
  const filePick = useRef(null);
  const filePending = useRef(null);
  const catIdxRef = useRef(0);
  const [fileUploading, setFileUploading] = useState(false);

  const uploadFile = useCallback(async (file) => {
    if (!filePending.current) return;
    const applyUrl = filePending.current;
    setFileUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authFetch('/api/files', { method: 'POST', body: fd });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || `업로드 실패 (${res.status})`); }
      const f = await res.json();
      applyUrl(f.url);
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e.message || '파일 업로드 실패');
    } finally {
      setFileUploading(false);
      filePending.current = null;
    }
  }, []);
  const { data: domainData, setField: setDomainField, saveSection: saveDomainSection, isDirty: domainIsDirty } =
    useSection('product:rugged', 'domain-header', 0);

  catIdxRef.current = catIdx;

  useEffect(() => {
    let alive = true;
    const prevIntNum = ref.current[catIdxRef.current]?.intNum ?? null;
    authFetch(`${API}?slug=${SLUG}`).then(r => r.json()).then(rows => {
      if (!alive) return;
      const parsed = (rows || []).map(parseRow);
      ref.current = parsed;
      setCats(parsed);
      if (prevIntNum != null) {
        const ni = parsed.findIndex(c => c.intNum === prevIntNum);
        if (ni >= 0) setCatIdx(ni);
      }
    }).catch(() => { if (alive) setCats([]); });
    return () => { alive = false; };
  }, [refreshKey]); // eslint-disable-line

  useEffect(() => { setSubcatIdx(0); setItemIdx(0); setSpecTabIdx(0); }, [catIdx]);
  useEffect(() => { setItemIdx(0); setSpecTabIdx(0); }, [subcatIdx]);
  useEffect(() => { setSpecTabIdx(0); }, [itemIdx]);

  const push = (next) => { dirtyRef.current = true; ref.current = next; setCats([...next]); };

  /* ── 대분류 ── */
  const addCat = () => {
    const usebn = window.confirm('BlockNote 에디터로 만들까요?\n확인: BlockNote (소프트웨어·서비스용)\n취소: 일반 양식 (하드웨어 제품용)');
    const n = newCat(usebn ? 'blocknote' : 'form');
    const next = [...ref.current, n];
    push(next); setCatIdx(next.length - 1);
  };
  const delCat = (ci) => {
    const cat = ref.current[ci];
    if (cat.intNum && !window.confirm('이 대분류를 삭제할까요?')) return;
    if (cat.intNum) delRef.current.push(cat.intNum);
    const next = ref.current.filter((_, i) => i !== ci);
    push(next);
    setCatIdx(t => Math.min(t, Math.max(0, next.length - 1)));
  };
  const updCat = (ci, val) => push(ref.current.map((c, i) => i === ci ? { ...c, category: val } : c));
  const reorderCat = (from, to) => {
    const next = [...ref.current]; const [m] = next.splice(from, 1); next.splice(to, 0, m);
    push(next); setCatIdx(to);
  };

  /* ── 소분류 ── */
  const addSubcat = (ci) => {
    const next = ref.current.map((c, i) => i === ci ? { ...c, subcats: [...c.subcats, newSubcat()] } : c);
    push(next); setSubcatIdx(next[ci].subcats.length - 1);
  };
  const delSubcat = (ci, si) => {
    push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.filter((_, j) => j !== si) } : c));
    setSubcatIdx(idx => Math.max(0, Math.min(idx, ref.current[ci].subcats.length - 1)));
  };
  const updSubcat = (ci, si, val) => push(ref.current.map((c, i) =>
    i === ci ? { ...c, subcats: c.subcats.map((sc, j) => j === si ? { ...sc, subcat: val } : sc) } : c));
  const reorderSubcat = useCallback((from, to) => {
    const ci = catIdx;
    const next = ref.current.map((c, i) => {
      if (i !== ci) return c;
      const subs = [...c.subcats]; const [m] = subs.splice(from, 1); subs.splice(to, 0, m);
      return { ...c, subcats: subs };
    });
    push(next); setSubcatIdx(to);
  }, [catIdx]); // eslint-disable-line

  const reorderItem = useCallback((from, to) => {
    const ci = catIdx;
    const si = subcatIdx;
    const next = ref.current.map((c, i) => {
      if (i !== ci) return c;
      return { ...c, subcats: c.subcats.map((sc, j) => {
        if (j !== si) return sc;
        const its = [...sc.items]; const [m] = its.splice(from, 1); its.splice(to, 0, m);
        return { ...sc, items: its };
      })};
    });
    push(next); setItemIdx(to);
  }, [catIdx, subcatIdx]); // eslint-disable-line

  /* ── 제품 ── */
  const addItem = (ci, si) => {
    const isBn = ref.current[ci]?.editorType === 'blocknote';
    const next = ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
      j === si ? { ...sc, items: [...sc.items, isBn ? newBnItem() : newItem()] } : sc) } : c);
    push(next); setItemIdx(next[ci].subcats[si].items.length - 1);
  };
  const delItem = (ci, si, ii) => {
    push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
      j === si ? { ...sc, items: sc.items.filter((_, k) => k !== ii) } : sc) } : c));
    setItemIdx(idx => Math.max(0, Math.min(idx, ref.current[ci].subcats[si].items.length - 1)));
  };
  const updItem = (ci, si, ii, field, val) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, [field]: val } : it) } : sc) } : c));

  /* ── 이미지 ── */
  const setImageKey = (ci, si, ii, imgIdx, key) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => { if (k !== ii) return it; const keys = [...it.imageKeys]; keys[imgIdx] = key; return { ...it, imageKeys: keys }; }) } : sc) } : c));
  const addImageSlot = (ci, si, ii) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: [...it.imageKeys, ''] } : it) } : sc) } : c));
  const delImageSlot = (ci, si, ii, imgIdx) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, imageKeys: it.imageKeys.filter((_, p) => p !== imgIdx) } : it) } : sc) } : c));

  /* ── 스펙시트 ── */
  const addSpecSheet = () => {
    const ci = catIdx, si = subcatIdx, ii = itemIdx;
    const cur = ref.current[ci]?.subcats[si]?.items[ii]?.specSheets ?? [];
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: [...it.specSheets, newSpecSheet()] })
    })}));
    setSpecTabIdx(cur.length);
  };
  const delSpecSheet = (pi) => {
    const ci = catIdx, si = subcatIdx, ii = itemIdx;
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: it.specSheets.filter((_, p) => p !== pi) })
    })}));
    setSpecTabIdx(t => Math.max(0, t - (pi <= t ? 1 : 0)));
  };
  const updSpecSheet = (pi, field, val) => {
    const ci = catIdx, si = subcatIdx, ii = itemIdx;
    push(ref.current.map((c, i) => i !== ci ? c : { ...c, subcats: c.subcats.map((sc, j) => j !== si ? sc : {
      ...sc, items: sc.items.map((it, k) => k !== ii ? it : { ...it, specSheets: it.specSheets.map((sh, p) => p !== pi ? sh : { ...sh, [field]: val }) })
    })}));
  };

  /* ── 링크 ── */
  const addLink = (ci, si, ii) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: [...it.links, newLink()] } : it) } : sc) } : c));
  const delLink = (ci, si, ii, li) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.filter((_, p) => p !== li) } : it) } : sc) } : c));
  const updLink = (ci, si, ii, li, field, val) => push(ref.current.map((c, i) => i === ci ? { ...c, subcats: c.subcats.map((sc, j) =>
    j === si ? { ...sc, items: sc.items.map((it, k) => k === ii ? { ...it, links: it.links.map((l, p) => p === li ? { ...l, [field]: val } : l) } : it) } : sc) } : c));

  /* ── 저장 ── */
  const save = useCallback(async () => {
    for (const id of delRef.current) {
      const r = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`삭제 실패 (${r.status})`);
    }
    delRef.current = [];
    for (let ci = 0; ci < ref.current.length; ci++) {
      const cat = ref.current[ci];
      const textCategory = JSON.stringify({
        editorType: cat.editorType || 'form',
        category: cat.category,
        subcats: cat.subcats.map(sc => ({
          subcat: sc.subcat,
          items: sc.items.map(it => it._bn
            ? { _bn: true, name: it.model, sectionContent: it.sectionContent || it.bnContent || '', bnContent: it.sectionContent || it.bnContent || '' }
            : {
                model: it.model, category: it.category,
                slug: toSlug(it.model),
                imageKeys: it.imageKeys.filter(Boolean),
                features: it.features.split('\n').map(s => s.trim()).filter(Boolean),
                tags: it.tags || [],
                specSheets: it.specSheets.map(s => ({ label: s.label, content: s.content })),
                links: it.links.map(l => ({ key: l.key, url: l.url })),
              }),
        })),
      });
      const r = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: cat.intNum || undefined, strSlug: SLUG, textCategory, sort: ci }),
      });
      if (!r.ok) throw new Error(`저장 실패 (${r.status})`);
      const saved = await r.json();
      if (!cat.intNum) ref.current = ref.current.map(x => x === cat ? { ...x, intNum: saved.intNum } : x);
    }
    await saveDomainSection();
    dirtyRef.current = false;
  }, [saveDomainSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);
  const isDirty = useCallback(() => dirtyRef.current || domainIsDirty(), [domainIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  /* ── 드래그 정렬 ── */
  const { containerRef: catTabsRef, dragProps: catDragProps, dropProps: catDropProps, cardClass: catCardClass } = useDragReorder(reorderCat);
  const { containerRef: subcatTabsRef, dragProps: subcatDragProps, dropProps: subcatDropProps, cardClass: subcatCardClass } = useDragReorder(reorderSubcat);
  const { containerRef: itemBarRef, dragProps: itemDragProps, dropProps: itemDropProps, cardClass: itemCardClass } = useDragReorder(reorderItem);

  if (cats === null) return <div className="rp-admin-wrap"><p style={{ color: '#888' }}>불러오는 중…</p></div>;

  const activeCat    = cats[catIdx];
  const isBlocknote  = activeCat?.editorType === 'blocknote';
  const subcats      = activeCat?.subcats ?? [];
  const activeSubcat = subcats[subcatIdx];
  const items        = activeSubcat?.items ?? [];
  const it           = items[itemIdx];

  return (
    <div className="rp-admin-wrap">
      <h2 className="rp-admin-title">Rugged Product</h2>

      {/* ── 도메인 헤더 ── */}
      <div style={{ marginBottom: 28, paddingBottom: 28, borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>도메인 헤더</span>
          <span style={{ fontSize: 12, color: '#888' }}>홈페이지 제품 목록 위에 표시되는 콘텐츠 (적용 분야, 소개 등)</span>
        </div>
        <SectionBuilder value={domainData.content || ''} onChange={(v) => setDomainField('content', v)} />
      </div>

      {/* ── 대분류 탭 ── */}
      <div className="rp-admin-tabrow">
        <ul className="rp-admin-tabs" ref={catTabsRef}>
          {cats.map((cat, ci) => (
            <li key={cat._id} data-flip={cat._id}
              className={`${ci === catIdx ? 'active' : ''} ${catCardClass(ci)}`}
              {...catDragProps(ci)} {...catDropProps(ci)}>
              <span className="rp-admin-tab-handle">⠿</span>
              <input className="rp-admin-tab-inp" value={cat.category} placeholder="대분류명"
                onClick={() => setCatIdx(ci)} onChange={(e) => updCat(ci, e.target.value)} />
              <button type="button" className="rp-admin-tab-del" onClick={() => delCat(ci)} title="대분류 삭제">×</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-tab" onClick={addCat}>+ 대분류</button>
      </div>

      {!activeCat ? (
        <div className="rp-admin-empty-state"><p>대분류를 먼저 추가하세요.</p></div>
      ) : (
      <>
      {/* ── 소분류 탭 ── */}
      <div className="rp-admin-subcatrow">
        <ul className="rp-admin-subcattabs" ref={subcatTabsRef}>
          {subcats.map((sc, si) => (
            <li key={sc._id} data-flip={sc._id}
              className={`${si === subcatIdx ? 'active' : ''} ${subcatCardClass(si)}`}
              {...subcatDragProps(si)} {...subcatDropProps(si)}>
              <span className="rp-admin-tab-handle" style={{ fontSize: '0.8rem', padding: '6px 3px 6px 6px' }}>⠿</span>
              <input className="rp-admin-subcattab-inp" value={sc.subcat} placeholder="소분류명"
                onClick={() => setSubcatIdx(si)} onChange={(e) => updSubcat(catIdx, si, e.target.value)} />
              <button type="button" className="rp-admin-tab-del" onClick={() => delSubcat(catIdx, si)}>×</button>
            </li>
          ))}
        </ul>
        <button type="button" className="rp-admin-add-subcattab" onClick={() => addSubcat(catIdx)}>+ 소분류</button>
      </div>

      {!activeSubcat ? (
        <div className="rp-admin-empty-state"><p>{isBlocknote ? '항목을 추가하세요.' : '소분류를 먼저 추가하세요.'}</p></div>
      ) : (
      <>
      {/* ── 제품 탭 바 ── */}
      <div className="rp-admin-item-bar" ref={itemBarRef}>
        {items.map((item, i) => (
          <div key={item._id} data-flip={item._id}
            className={`rp-admin-item-tab ${i === itemIdx ? 'active' : ''} ${itemCardClass(i)}`}
            {...itemDragProps(i)} {...itemDropProps(i)}>
            <span className="rp-admin-tab-handle">⠿</span>
            <button type="button" onClick={() => setItemIdx(i)}>
              {item.model || `${isBlocknote ? '항목' : '제품'} ${i + 1}`}
            </button>
          </div>
        ))}
        <button type="button" className="rp-admin-item-add" onClick={() => addItem(catIdx, subcatIdx)}>+ {isBlocknote ? '항목' : '제품'} 추가</button>
      </div>

      {/* ── 제품 에디터 ── */}
      {it ? (
        isBlocknote ? (
        /* ── SectionBuilder 에디터 (소프트웨어·서비스용) ── */
        <div className="rp-admin-editor">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <input className="rp-admin-field-inp" style={{ flex: 1 }}
              value={it.model} placeholder="항목 이름"
              onChange={(e) => updItem(catIdx, subcatIdx, itemIdx, 'model', e.target.value)} />
            <button type="button" className="rp-admin-item-del" onClick={() => delItem(catIdx, subcatIdx, itemIdx)}>삭제 ×</button>
          </div>
          <SectionBuilder
            key={it._id}
            value={it.sectionContent || it.bnContent || ''}
            onChange={(json) => {
              updItem(catIdx, subcatIdx, itemIdx, 'sectionContent', json);
              updItem(catIdx, subcatIdx, itemIdx, 'bnContent', json);
            }}
          />
        </div>
        ) : (
        /* ── 일반 양식 에디터 (하드웨어 제품용) ── */
        <div className="rp-admin-editor">
          <div className="rp-admin-top">
            {/* 이미지 갤러리 */}
            <div className="rp-admin-gallery">
              <div className="rp-admin-gallery-label">
                이미지 갤러리 ({it.imageKeys.length}/6)
                {it.imageKeys.length < 6 && (
                  <button type="button" className="rp-admin-gallery-add" onClick={() => addImageSlot(catIdx, subcatIdx, itemIdx)}>+ 추가</button>
                )}
              </div>
              <div className="rp-admin-gallery-grid">
                {it.imageKeys.map((key, imgIdx) => (
                  <div key={key || `slot-${imgIdx}`} className="rp-admin-gallery-slot">
                    <button type="button" className="rp-admin-gallery-del" onClick={() => delImageSlot(catIdx, subcatIdx, itemIdx, imgIdx)}>×</button>
                    <ImageDrop value={key} onChange={(k) => setImageKey(catIdx, subcatIdx, itemIdx, imgIdx, k)} />
                  </div>
                ))}
                {it.imageKeys.length === 0 && (
                  <div className="rp-admin-gallery-empty" onClick={() => addImageSlot(catIdx, subcatIdx, itemIdx)}>
                    <span>+ 이미지 추가</span>
                  </div>
                )}
              </div>
            </div>

            {/* 기본정보 */}
            <div className="rp-admin-info">
              <button type="button" className="rp-admin-item-del" onClick={() => delItem(catIdx, subcatIdx, itemIdx)}>제품 삭제 ×</button>
              <label className="rp-admin-field-label">모델명</label>
              <input className="rp-admin-field-inp" value={it.model} placeholder="ACE-R-XXXX-G11"
                onChange={(e) => updItem(catIdx, subcatIdx, itemIdx, 'model', e.target.value)} />
              <label className="rp-admin-field-label">카테고리 / 폼팩터</label>
              <input className="rp-admin-field-inp" value={it.category} placeholder="2U/2P Xeon SP Short-Depth Military Rugged HPC"
                onChange={(e) => updItem(catIdx, subcatIdx, itemIdx, 'category', e.target.value)} />
              <label className="rp-admin-field-label">주요 특징 (한 줄에 하나씩)</label>
              <textarea className="rp-admin-profile-inp" value={it.features} rows={6}
                placeholder={"Dual Intel® 4th/5th Xeon Scalable Processor CPUs up to 60 Cores\n32 x DDR5 DIMM Slot, 256GB RDIMM up to 8TB\n..."}
                onChange={(e) => updItem(catIdx, subcatIdx, itemIdx, 'features', e.target.value)} />
              <label className="rp-admin-field-label">태그 (필터·검색용, Enter로 추가)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {(it.tags || []).map((tag, ti) => (
                  <span key={ti} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', background: '#e0e7ff', borderRadius: 12, fontSize: 13 }}>
                    {tag}
                    <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: '#6366f1' }}
                      onClick={() => updItem(catIdx, subcatIdx, itemIdx, 'tags', (it.tags || []).filter((_, i) => i !== ti))}>×</button>
                  </span>
                ))}
              </div>
              <input className="rp-admin-field-inp" placeholder="태그 입력 후 Enter"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const v = e.target.value.trim();
                    if (!(it.tags || []).includes(v)) updItem(catIdx, subcatIdx, itemIdx, 'tags', [...(it.tags || []), v]);
                    e.target.value = '';
                  }
                }} />
              <label className="rp-admin-field-label">링크 버튼 (최대 3개)</label>
              {it.links.map((lnk, li) => (
                <div key={lnk._id} className="rp-admin-link-row">
                  <input className="rp-admin-link-key" value={lnk.key} placeholder="Datasheet"
                    onChange={(e) => updLink(catIdx, subcatIdx, itemIdx, li, 'key', e.target.value)} />
                  <input className="rp-admin-link-url" value={lnk.url} placeholder="https://..."
                    onChange={(e) => updLink(catIdx, subcatIdx, itemIdx, li, 'url', e.target.value)} />
                  <button type="button" className="rp-admin-spec-del" onClick={() => delLink(catIdx, subcatIdx, itemIdx, li)}>×</button>
                </div>
              ))}
              {it.links.length < 3 && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="rp-admin-add-btn" onClick={() => addLink(catIdx, subcatIdx, itemIdx)}>+ 링크 추가</button>
                  <button type="button" className="rp-admin-add-btn" disabled={fileUploading}
                    onClick={() => {
                      filePending.current = (url) => push(ref.current.map((c, ci2) => ci2 !== catIdx ? c : { ...c, subcats: c.subcats.map((sc, j) =>
                        j !== subcatIdx ? sc : { ...sc, items: sc.items.map((it2, k) =>
                          k !== itemIdx ? it2 : { ...it2, links: [...it2.links, { _id: uid(), key: 'Information', url }] }
                        )}
                      )}));
                      filePick.current?.click();
                    }}>
                    {fileUploading ? '업로드 중…' : '+ 파일 추가'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* ── 스펙시트 탭 ── */}
          <div className="rp-admin-detail-tabs">
            {(it.specSheets || []).map((sheet, pi) => (
              <div key={sheet._id} className="rp-admin-tab-wrap">
                <button type="button"
                  className={`rp-admin-detail-tab-btn${specTabIdx === pi ? ' active' : ''}`}
                  onClick={() => setSpecTabIdx(pi)}>
                  {sheet.label}
                </button>
                {(it.specSheets || []).length > 1 && (
                  <button type="button" className="rp-admin-tab-del-btn"
                    onClick={() => delSpecSheet(pi)}>×</button>
                )}
              </div>
            ))}
            <button type="button" className="rp-admin-tab-add-btn" onClick={addSpecSheet}>+ 탭</button>
          </div>

          {it.specSheets?.[specTabIdx] && (
            <div className="rp-admin-section">
              <input className="rp-admin-field-inp rp-admin-sheet-label-inp"
                value={it.specSheets[specTabIdx].label}
                onChange={(e) => updSpecSheet(specTabIdx, 'label', e.target.value)}
                placeholder="탭 이름" />
              <SectionBuilder
                key={it.specSheets[specTabIdx]._id}
                value={it.specSheets[specTabIdx].content}
                onChange={(json) => updSpecSheet(specTabIdx, 'content', json)}
              />
            </div>
          )}
        </div>
        )
      ) : (
        <div className="rp-admin-empty-state">
          <p>이 소분류에 {isBlocknote ? '항목이' : '제품이'} 없습니다.</p>
          <button type="button" className="rp-admin-add-btn" onClick={() => addItem(catIdx, subcatIdx)}>+ {isBlocknote ? '항목' : '제품'} 추가</button>
        </div>
      )}
      </>
      )}
      </>
      )}

      {onBack && (
        <div style={{ marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onBack}>← 응용 분야로 돌아가기</button>
        </div>
      )}

      <input ref={filePick} type="file" accept=".pdf,.xlsx,.xls,.docx,.doc,.zip" style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.target.value = ''; }} />
    </div>
  );
}
